import React, { FunctionComponent, ReactNode, useState } from 'react'

import { OccContextComponent, CaptchaType, GoogleRecaptchaV2Size, GoogleRecaptchaV2Theme } from '@vista-digital/occ-ui'
import { ApiConfig, Language, LoyaltyMemberIdentifier } from '@vista-digital/occ-domain'
import { NestedPartial } from '@vista-digital/utilities'

import { cmsDataService, memberService } from '../../services'
import { LoginMethod, LoginMethodContext } from './loginMethod'
import { CustomShowtimeButton } from '../dateShowtimePicker/subcomponents'
import {
    getAssets,
    getFilmConfig,
    getFilterConfig,
    getFormValidationConfig,
    getMemberConfig,
    getOccAccessibilityText,
    getOccContextDisplayText,
    getOccLocale,
    getOrderConfig,
    getTicketRedemptionCodeTypes,
} from './props'

export const OccContext: FunctionComponent<{ children: NonNullable<ReactNode> }> = props => {
    const data = cmsDataService.get()

    const api: NestedPartial<ApiConfig> = {
        url: data.api.apiUrl,
        authTokenProvider: () => data.api.authToken, // TODO: make it refresh
        // regionCode: 'UK',
    }
    const cdn = {
        vista: { url: data.api.vistaCdn },
        moviexchange: { url: data.api.movieExchangeCdn },
    }

    const languages: Language[] = [
        {
            languageTag: 'en',
            label: 'English (US)',
            iconName: 'american-flag',
            isDefault: true,
        },
    ]

    const [loginMethod, setLoginMethod] = useState(LoyaltyMemberIdentifier.Email)

    const contextValues: LoginMethod = {
        setLoginMethod: method => {
            setLoginMethod(method)
        },
        getLoginMethod: () => loginMethod,
    }

    return (
        <LoginMethodContext.Provider value={contextValues}>
            <OccContextComponent
                domain={{
                    api: api,
                    languages: languages,
                    browsing: { maximumSelectedSites: 5 },
                    cache: { durations: { showtimes: 20, films: 20, items: 20, sites: 20 } },
                    cdn,
                    ticketRedemptionCodeTypes: getTicketRedemptionCodeTypes(),
                    cxm: { enabled: false },
                    loyalty: {
                        onSignedOut: () => memberService.onSignedOut(),
                        authentication: {
                            // TODO: Might need to switch to username identifiers
                            memberIdentifier: loginMethod,
                        },
                    },
                    order: getOrderConfig(data.occConfiguration),
                }}
                config={{
                    enableSkeletonLoadingIndicators: true,
                    locale: getOccLocale(data.occConfiguration),
                    security: {
                        captcha: {
                            type: CaptchaType.GoogleRecaptchaV2,
                            siteKey: data.captcha?.siteKey || undefined,
                            size: GoogleRecaptchaV2Size.Normal,
                            theme: GoogleRecaptchaV2Theme.Light,
                        },
                    },
                    form: {
                        enableFloatingInputLabels: false,
                        fieldValidation: getFormValidationConfig(data.occConfiguration),
                    },
                    film: getFilmConfig(data.occConfiguration),
                    filter: getFilterConfig(data.occConfiguration),
                    member: getMemberConfig(data.occConfiguration),
                    notification: { dismissalTime: 3000 },
                    seating: {
                        screen: {
                            height: 150,
                        },
                    },
                }}
                displayText={getOccContextDisplayText(data.occConfiguration)}
                accessibilityText={getOccAccessibilityText(data.occConfiguration)}
                assets={getAssets(data.occConfiguration)}
                subcomponents={{
                    ShowtimeButton: CustomShowtimeButton,
                }}
                // placeholderAssets={}
            >
                {props.children}
            </OccContextComponent>
        </LoginMethodContext.Provider>
    )
}
